import { Tooltip, Modal } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RiEmojiStickerLine } from "react-icons/ri";
import { assetContent } from "../../../store/reducers/asset";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { encodeAssetUrl } from "../../../utils/format.utils";
import AssetModal from "../../../pages/WhiteBoard/WhiteBoardInput/AssetModal";
import EmojiPicker from "./Emoji";
import {
  ActionButton,
  Container,
  EmojiSelector,
  Input,
  InputForm,
  InputOptions,
} from "./ChatInput.styles";
import useCryptography from "../../../hooks/useCryptography";
import { updateRoomMessages } from "../../../store/reducers/dmState";
import { AuthenticateState } from "../../../types/types";
import { useDMWebsocket } from "../../../hooks/useDMWebsocket";

type Prop = {
  lowerCaseToAddress: string;
  isChatting: boolean;
  targetPubkey: Uint8Array;
  setlistModalstatus: any;
  setAsset: any;
};
const ChatInput: React.FC<Prop> = ({
  lowerCaseToAddress,
  isChatting,
  targetPubkey,
  setlistModalstatus,
  setAsset,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { secondFontColor, backgroundColor, fontColor, borderColor } =
    useAppSelector((state) => state.layout);
  const { authenticateStatus } = useAppSelector((state) => state.auth);
  const [showEmojiSelector, setShowEmojiSelector] = useState(false);
  const [inputText, setInputText] = useState("");
  const isUserTyping = useMemo(() => !!inputText, [inputText]);
  const { generateMessage } = useCryptography();
  const { sendTyping, sendStopTyping, sendMessage } = useDMWebsocket();
  useEffect(() => {
    if (isUserTyping) sendTyping(lowerCaseToAddress);
    else sendStopTyping(lowerCaseToAddress);
  }, [isUserTyping]);

  const send = (plaintextMessage: string) => {
    const message = generateMessage(
      plaintextMessage,
      targetPubkey,
      lowerCaseToAddress
    );
    dispatch(
      updateRoomMessages({
        toAddress: lowerCaseToAddress,
        message: { ...message, plaintextMessage },
      })
    );
    sendMessage(message);
  };

  const onSubmit = (e: any) => {
    e.preventDefault();
    send(inputText);
    setInputText("");
  };

  const handleKeyDown = (e: any) => {
    if (e.keyCode === 13 && !e.shiftKey) {
      e.preventDefault();
      if (inputText && isChatting) {
        onSubmit(e);
      }
    }
  };
  const onEmojiSelect = (emoji: any) => {
    setInputText((text) => text + emoji.native);
  };

  const onAssetSelect = (asset: assetContent) => {
    send(encodeAssetUrl(`${asset.imageUrl},${asset.permalink}`));
  };

  return (
    <Container>
      <InputForm
        onSubmit={(e) => {
          if (isChatting) {
            onSubmit(e);
          }
        }}
      >
        <Modal
          BackdropProps={{ style: { backgroundColor: "transparent" } }}
          open={showEmojiSelector}
          onClose={() => setShowEmojiSelector(false)}
        >
          <EmojiSelector>
            <EmojiPicker
              categories={[
                "frequent",
                "people",
                "nature",
                "foods",
                "activity",
                "places",
                "objects",
                "symbols",
                "flags",
              ]}
              previewPosition={"none"}
              searchPosition={"none"}
              skinTonPosition={"none"}
              navPosition={"bottom"}
              theme={"dark"}
              perLine={6}
              maxFrequentRows={1}
              emojiSize={17}
              onEmojiSelect={onEmojiSelect}
            />
          </EmojiSelector>
        </Modal>
        <InputOptions>
          <Input
            iffontcolor={fontColor}
            ifbackgoundcolor={backgroundColor}
            ifbordercolor={borderColor}
            maxRows={4}
            minRows={1}
            onKeyDown={(e) => handleKeyDown(e)}
            onChange={(e) => {
              const text = e.target.value.replace(/^(\r\n|\n|\r|\t| )+/gm, "");
              setInputText(text);
            }}
            value={inputText}
            disabled={!isChatting}
            placeholder={t("SocialConnection_Chat_Placeholder")}
          />
          {authenticateStatus === AuthenticateState.WALLET && (
            <AssetModal
              onSubmit={onAssetSelect}
              submitNewMessage={send}
              setlistModalstatus={setlistModalstatus}
              setAsset={setAsset}
            />
          )}

          <Tooltip title={t("FlashChat_Actions_Emojis")} arrow disableInteractive placement="top-end">
            <ActionButton
              ifSecondFontColor={secondFontColor}
              onClick={() => setShowEmojiSelector(!showEmojiSelector)}
            >
              <RiEmojiStickerLine size={22}/>
            </ActionButton>
          </Tooltip>
        </InputOptions>
      </InputForm>
    </Container>
  );
};

export default ChatInput;
